'use client';
import { Box, type SxProps, Typography } from '@mui/material';
import { useId } from 'react';
import { ElementViewed, useElementViewedContext } from '@repo/segment/Analytics/ElementViewed';
import type {
  AboveReadMoreContent,
  BelowReadMoreContent,
} from '@repo/supergraph-schemas/roundup/base/types';
import { StructuredContentRenderer } from '../../../StructuredContentRenderer';
import { MAX_EXPANDED_HEIGHT } from './constants';
import { useCustomAboveSectionsToggleHandlers } from './useCustomAboveSectionsToggleHandlers';

export type ReadMoreContentProps = {
  aboveReadMoreContent: AboveReadMoreContent;
  belowReadMoreContent?: BelowReadMoreContent;
};

const readMoreStyles: SxProps = {
  fontWeight: 'bold',
  cursor: 'pointer',
  color: 'limes.tintLP2',
  alignItems: 'center',
  display: 'inline-flex',
  maxWidth: 'inherit',
  gap: 0.5,
  '&:hover': {
    textDecoration: 'underline',
    textDecorationColor: 'limes.tintLP2',
  },
};

const firstContentStyles: SxProps = {
  display: 'inline',
  fontSize: 'inherit',
  lineHeight: 'inherit',
  '& *': {
    display: 'inline',
    fontSize: 'inherit',
    lineHeight: 'inherit',
  },
};

const labelStyles: SxProps = {
  display: 'inline-flex',
  maxWidth: 'fit-content',
  verticalAlign: 'baseline',
  marginLeft: 1,
};

type ReadMoreBodyProps = ReadMoreContentProps & { idSuffix: string };

function ReadMoreBody({ idSuffix, aboveReadMoreContent, belowReadMoreContent }: ReadMoreBodyProps) {
  const { fireElementInteracted } = useElementViewedContext();
  const { handleReadMoreClick, handleReadMoreKeyDown, handleReadLessClick, handleReadLessKeyDown } =
    useCustomAboveSectionsToggleHandlers(idSuffix, fireElementInteracted);

  return (
    <>
      <input
        type="checkbox"
        className="checkbox-toggle"
        id={`checkbox-toggle-${idSuffix}`}
        defaultChecked={false}
        hidden
      />
      <Box className="first-content" component="span" sx={firstContentStyles}>
        <StructuredContentRenderer doc={aboveReadMoreContent} />

        {belowReadMoreContent && (
          <Box
            component="label"
            className="read-more-label"
            htmlFor={`checkbox-toggle-${idSuffix}`}
            aria-controls={`remaining-content-${idSuffix}`}
            tabIndex={0}
            sx={labelStyles}
            onClick={handleReadMoreClick}
            onKeyDown={handleReadMoreKeyDown}
            role="button"
            aria-expanded={false}
          >
            <Typography
              className="read-more"
              component="span"
              variant="textSmall"
              sx={readMoreStyles}
            >
              Read More
            </Typography>
          </Box>
        )}
      </Box>

      {belowReadMoreContent && (
        <>
          <Box className="remaining-content" id={`remaining-content-${idSuffix}`}>
            <StructuredContentRenderer doc={belowReadMoreContent} />
          </Box>

          <Box
            className="read-less-container"
            component="label"
            htmlFor={`checkbox-toggle-${idSuffix}`}
            aria-controls={`remaining-content-${idSuffix}`}
            tabIndex={0}
            onClick={handleReadLessClick}
            onKeyDown={handleReadLessKeyDown}
            aria-expanded={true}
            role="button"
          >
            <Typography component="span" variant="text" sx={readMoreStyles}>
              Show Less
            </Typography>
          </Box>
        </>
      )}
    </>
  );
}

export function CustomAboveSectionsReadMore({
  aboveReadMoreContent,
  belowReadMoreContent,
}: ReadMoreContentProps) {
  const idSuffix = useId();

  return (
    <ElementViewed entityName="simplified_ru" sectionName="banking">
      <Box
        sx={{
          '& a, & .MuiLink-root': {
            color: 'limes.tintLP2',
          },
          '& .checkbox-toggle:checked ~ .first-content .read-more-label': {
            display: 'none',
          },
          '& details > summary:hover': {
            backgroundColor: 'transparent',
            outline: 'none',
          },
          // Always render remaining content but use max-height to hide when collapsed
          // This keeps content in layout flow to prevent layout shift
          '& .remaining-content': {
            display: 'block',
            maxHeight: 0,
            overflow: 'hidden',
            visibility: 'hidden',
            mt: 0,
          },
          '& .checkbox-toggle:checked ~ .remaining-content': {
            maxHeight: MAX_EXPANDED_HEIGHT,
            overflow: 'visible',
            visibility: 'visible',
            mt: 3.5,
          },
          // Always render read less button but use max-height to hide when collapsed
          '& .read-less-container': {
            display: 'flex',
            maxHeight: 0,
            overflow: 'hidden',
            visibility: 'hidden',
            maxWidth: 'fit-content',
            mt: 0,
            margin: 0,
            padding: 0,
          },
          '& .checkbox-toggle:checked ~ .read-less-container': {
            maxHeight: MAX_EXPANDED_HEIGHT,
            overflow: 'visible',
            visibility: 'visible',
            mt: 3.5,
          },
        }}
      >
        <ReadMoreBody
          idSuffix={idSuffix}
          aboveReadMoreContent={aboveReadMoreContent}
          belowReadMoreContent={belowReadMoreContent}
        />
      </Box>
    </ElementViewed>
  );
}
